import classNames from "classnames";
import { useField } from "formik";
import { Editor } from "neetoeditor";
import { ImageUploader } from "neetoimageuploader";
import { Input } from "neetoui/formik";
import { isEmpty } from "ramda";

import Checkbox from "./Checkbox";
import { IMAGE_UPLOAD_CONFIG } from "./constants";
import DragHandle from "./DragHandle";
import OptionActions from "./OptionActions";

const ImageOption = ({
  id,
  name,
  optionIndex,
  disabled,
  placeholder,
  shouldFocus,
  onKeyDown,
  onChange,
  onBlur,
  onDelete,
  onClone,
  arrayHelpers,
  hideDeleteOption = false,
  hideCloneOption = false,
  innerRef,
  draggableProps,
  dragHandleProps,
  isDraggable,
  isCheckboxEnabled,
  isEditor,
}) => {
  const imageFieldName = name.replace(".label", ".image");

  const [field] = useField(name);
  const [_, { value: image }, { setValue }] = useField(imageFieldName);

  const handleImageChange = changedImage => {
    const imageValue = isEmpty(changedImage?.url) ? null : changedImage;
    setValue(imageValue, false);
    onChange({ id, value: imageValue, isOnBlur: false, fieldType: "image" });
  };

  return (
    <div
      ref={innerRef}
      {...{ ...draggableProps, ...dragHandleProps }}
      data-testid="form-block-options"
    >
      <div className="nf-input-options group relative mb-2 flex items-start gap-1">
        {isDraggable && (
          <DragHandle
            {...dragHandleProps}
            data-testid={`drag-handle-${optionIndex}`}
          />
        )}
        {isCheckboxEnabled && <Checkbox {...{ name, optionIndex }} />}
        <div
          className={classNames("flex w-full items-center justify-center", {
            "pointer-events-none opacity-80": disabled,
          })}
        >
          <div className="neeto-ui-rounded-lg neeto-ui-border-gray-200 neeto-molecules-option-fields__image-option neeto-ui-bg-white group relative flex w-full flex-col gap-2 border p-2 pe-8">
            <div
              className="neeto-molecules-option-fields__image-uploader-container flex-shrink-0"
              data-testid={`picture-choice-option-${optionIndex}`}
            >
              <ImageUploader
                className="neeto-molecules-option-fields__image-uploader w-full"
                isOptionsDisabled={disabled}
                key={image?.url}
                src={image?.url}
                uploadConfig={IMAGE_UPLOAD_CONFIG}
                onUploadComplete={handleImageChange}
              />
            </div>
            <div className="w-full">
              {isEditor ? (
                <Editor
                  {...{ disabled, name, placeholder }}
                  autoFocus={shouldFocus}
                  className="flex-1"
                  data-testid={`editor-option-${optionIndex}`}
                  initialValue={field.value}
                  onChange={value => onChange({ id, value })}
                  onBlur={value => {
                    onChange({ id, value, isOnBlur: true });
                    onBlur?.(id, value);
                  }}
                />
              ) : (
                <Input
                  {...{ disabled, name, onBlur, onKeyDown, placeholder }}
                  autoFocus={shouldFocus}
                  data-testid={`option-input-${optionIndex}`}
                  onFocus={({ target }) => target.select()}
                  onBlur={(event, ...args) => {
                    onChange({ id, value: event.target.value, isOnBlur: true });
                    onBlur?.(id, event.target.value);
                    field.onBlur(event, ...args);
                  }}
                  onChange={({ target }) =>
                    onChange({ id, value: target.value })
                  }
                />
              )}
            </div>
            <div className="absolute end-2 top-2">
              <OptionActions
                {...{
                  arrayHelpers,
                  disabled,
                  hideCloneOption,
                  hideDeleteOption,
                  id,
                  onClone,
                  onDelete,
                  optionIndex,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageOption;
